import { useState, useEffect } from 'react';
import { apiFetch } from './api';

export default function PositionSettings() {
  const [positions, setPositions] = useState([]);
  const [newValue, setNewValue] = useState('');
  const [newColor, setNewColor] = useState('#3b82f6');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function loadData() {
    try {
      setLoading(true);
      setError('');
      const data = await apiFetch('/positions');
      setPositions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  async function addPosition() {
    if (!newValue.trim()) {
      alert('Please enter a position name');
      return;
    }

    try {
      await apiFetch('/positions', {
        method: 'POST',
        body: JSON.stringify({ name: newValue.trim(), color: newColor })
      });
      setNewValue('');
      setNewColor('#3b82f6');
      await loadData();
    } catch (e) {
      alert(e.message || 'Failed to add position');
    }
  }

  async function updatePositionColor(id, color) {
    try {
      await apiFetch(`/positions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ color })
      });
      await loadData();
    } catch (e) {
      alert(e.message || 'Failed to update position color');
    }
  }

  async function deletePosition(id, name) {
    const confirmed = window.confirm(`Delete position "${name}"?`);
    if (!confirmed) return;

    try {
      await apiFetch(`/positions/${id}`, {
        method: 'DELETE'
      });
      await loadData();
    } catch (e) {
      alert(e.message || 'Failed to delete position');
    }
  }

  return (
    <div className="card boat-config-card-compact">
      <div className="boat-config-header-compact">
        <div>
          <h2 className="h2" style={{ marginTop: 0, marginBottom: 4 }}>Position Configuration</h2>
          <p className="help" style={{ margin: 0 }}>
            Manage rowing positions with color coding for the lineup builder
          </p>
        </div>
        {loading && <span className="help">Loading…</span>}
        {error && <span className="help boat-config-error">{error}</span>}
      </div>

      <div className="boat-config-form-compact">
        <input
          className="input"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="e.g., Port, Starboard, Cox, Both"
          onKeyDown={(e) => e.key === 'Enter' && addPosition()}
          style={{ flex: 1, minWidth: 150 }}
        />

        <input
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          title="Position color"
          style={{ width: 50, height: 36, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
        />

        <button className="btn" onClick={addPosition} style={{ whiteSpace: 'nowrap' }}>
          + Add
        </button>
      </div>

      <div className="boat-config-tab-content">
        {positions.length === 0 ? (
          <p className="help" style={{ textAlign: 'center', padding: 16 }}>
            No positions configured yet. Add some above!
          </p>
        ) : (
          <ul className="list" style={{ margin: 0 }}>
            {positions.map((position) => (
              <li key={position.id} className="boat-config-item-compact">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="color"
                    value={position.color || '#3b82f6'}
                    onChange={(e) => updatePositionColor(position.id, e.target.value)}
                    title="Position color"
                    style={{ width: 30, height: 30, cursor: 'pointer', border: '1px solid #ccc', borderRadius: 4 }}
                  />
                  <span>{position.name}</span>
                </div>
                <button
                  type="button"
                  className="btn-icon danger"
                  onClick={() => deletePosition(position.id, position.name)}
                  title="Delete position"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="boat-config-hint-compact">
        <div className="boat-config-hint-content">
          <p className="help" style={{ margin: 0 }}>
            <strong>Position colors</strong> are used in the Lineup Builder to visually distinguish athletes by their rowing position.
            <br />
            Common positions: Port, Starboard, Cox, Both/Sculling
          </p>
        </div>
      </div>

      <div className="boat-config-hint-compact" style={{ marginTop: 8, borderLeft: '3px solid #f59e0b' }}>
        <div className="boat-config-hint-content">
          <p className="help" style={{ margin: 0, color: '#f59e0b' }}>
            <strong>Important:</strong> Any new Position values added here must also be added to Salesforce to keep data in sync. Contact your Salesforce administrator to update the corresponding picklist fields.
          </p>
        </div>
      </div>
    </div>
  );
}
